<script setup lang="ts">
import LeDownArrow from "@dsk/icons/LeDownArrow.vue";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";

import LeResultsListItem from "@/components/dictionary-selector/LeResultsListItem.vue";
import LeSkeleton from "@/components/LeSkeleton.vue";
import { useComponentScope, useRestoreScroll } from "@/composables";
import { MatomoHelper } from "@/helpers";
import { type Dictionary, type DictionaryGroupWithDictionaries, MatomoEventAction, MatomoEventCategory } from "@/types";

interface Props {
  companyDictionaries?: Dictionary[];
  companyDictionaryGroupHeading: string;
  favoriteDictionaries?: Dictionary[];
  suggestedDictionaries?: Dictionary[];
  resultDictionaries?: Dictionary[];
  resultUpsellDictionaries?: Dictionary[];
  resultUpsellUnlockedDictionaries?: Dictionary[];
  selectedDictionary?: Dictionary | undefined;
  groupsWithDictionaries?: DictionaryGroupWithDictionaries[];
}

const props = withDefaults(defineProps<Props>(), {
  companyDictionaries: () => [],
  favoriteDictionaries: () => [],
  suggestedDictionaries: () => [],
  resultDictionaries: () => [],
  resultUpsellDictionaries: () => [],
  resultUpsellUnlockedDictionaries: () => [],
  groupsWithDictionaries: () => [],
  selectedDictionary: undefined
});

const emit = defineEmits<{
  select: [dictionaryId: string];
  favorite: [dictionaryId: string];
  unfavorite: [dictionaryId: string];
}>();

const toast = useToast();
const { t } = useI18n();
const { scope } = useComponentScope();

const popoverContent = ref<HTMLElement>();
const { restoreScroll } = useRestoreScroll(popoverContent.value);

const isSelected = (dictionaryId: string): boolean => dictionaryId === props.selectedDictionary?.id;
const isFavorite = (dictionaryId: string): boolean => {
  const favoriteDictionary = props.favoriteDictionaries.find((dictionary) => dictionary.id === dictionaryId);

  return favoriteDictionary !== undefined;
};
const hasResult = (dictionaryId: string) => props.resultDictionaries.find((d) => d.id === dictionaryId) !== undefined;

const favorite = (dictionaryId: string) => {
  MatomoHelper.sendEvent(MatomoEventCategory.NarrowDictionaries, MatomoEventAction.AddFavorite, "Added favorite");

  restoreScroll(() => {
    emit("favorite", dictionaryId);
  });
};

const unfavorite = (dictionaryId: string) => {
  MatomoHelper.sendEvent(MatomoEventCategory.NarrowDictionaries, MatomoEventAction.RemoveFavorite, "Removed favorite");

  restoreScroll(() => {
    emit("unfavorite", dictionaryId);
  });
};

const select = (dictionaryId: string, closeFn?: () => void) => {
  MatomoHelper.sendEvent(
    MatomoEventCategory.NarrowDictionaries,
    MatomoEventAction.SelectDictionaryNarrow,
    `${hasResult(dictionaryId) ? "Dictionary with result" : "Dictionary with no result"}`
  );

  toast.clear();
  emit("select", dictionaryId);
  closeFn?.();
};

const selectSuggestedDictionary = (dictionaryId: string, dictionaryNumber: number, closeFn?: () => void) => {
  MatomoHelper.sendEvent(
    MatomoEventCategory.NarrowDictionaries,
    MatomoEventAction.SelectDictionaryNarrow,
    `Suggested dictionary number ${dictionaryNumber}`
  );

  toast.clear();
  emit("select", dictionaryId);
  closeFn?.();
};

const clickedDropdownDictionariesMatomoTracker = (isOpen: boolean) => {
  // We dont track close action
  if (isOpen) {
    return;
  }

  MatomoHelper.sendEvent(
    MatomoEventCategory.NarrowDictionaries,
    MatomoEventAction.OpenNarrowDictionarySelector,
    "Clicked narrow dictionaries"
  );
};
</script>

<template>
  <Popover v-slot="{ open, close }" :[scope]="scope">
    <PopoverButton as="template">
      <button class="le-selected-dictionary" @click="clickedDropdownDictionariesMatomoTracker(open)">
        <span class="le-selected-dictionary__item">
          <span class="le-selected-dictionary__item-title">
            <span v-if="props.selectedDictionary">{{ props.selectedDictionary.title }}</span>
            <LeSkeleton v-else width="55%" height="16px" />
          </span>
          <br />
          <span class="le-selected-dictionary__item-publisher">
            <span v-if="props.selectedDictionary">{{ props.selectedDictionary.publisher }}</span>
            <LeSkeleton v-else width="35%" height="14px" />
          </span>
        </span>
        <span class="le-selected-dictionary__icon">
          <LeDownArrow />
        </span>
      </button>
    </PopoverButton>

    <Transition name="le-narrow-dictionary-selector__transition">
      <PopoverPanel class="le-narrow-dictionary-selector-container">
        <div ref="popoverContent" class="le-narrow-dictionary-selector">
          <div v-if="props.companyDictionaries.length > 0" class="le-narrow-dictionary-selector__collection">
            <h3 class="le-narrow-dictionary-selector__collection-header">
              {{ props.companyDictionaryGroupHeading }}
            </h3>
            <div class="le-narrow-dictionary-selector__collection-content">
              <LeResultsListItem
                v-for="dictionary in props.companyDictionaries"
                :key="dictionary.id"
                class="le-narrow-dictionary-selector__item"
                :dictionary="dictionary"
                :selected-dictionary="isSelected(dictionary.id)"
                :is-selected="isSelected(dictionary.id)"
                :is-favorite="isFavorite(dictionary.id)"
                @select="select(dictionary.id, close)"
              />
            </div>
          </div>

          <div v-if="props.favoriteDictionaries.length > 0" class="le-narrow-dictionary-selector__collection">
            <h3 class="le-narrow-dictionary-selector__collection-header">
              {{ t("le-narrow-dictionary-selector.favorites-collection.header") }}
            </h3>
            <div class="le-narrow-dictionary-selector__collection-content">
              <LeResultsListItem
                v-for="dictionary in props.favoriteDictionaries"
                :key="dictionary.id"
                show-favorite-toggle
                class="le-narrow-dictionary-selector__item"
                :dictionary="dictionary"
                :is-selected="isSelected(dictionary.id)"
                :is-favorite="isFavorite(dictionary.id)"
                @favorite="favorite(dictionary.id)"
                @unfavorite="unfavorite(dictionary.id)"
                @select="select(dictionary.id, close)"
              />
            </div>
          </div>

          <div
            v-if="props.suggestedDictionaries.length > 0 || props.resultUpsellDictionaries.length > 0"
            class="le-narrow-dictionary-selector__collection"
          >
            <h3 class="le-narrow-dictionary-selector__collection-header">
              {{ t("le-narrow-dictionary-selector.results-collection.header") }}
            </h3>
            <div class="le-narrow-dictionary-selector__collection-break"></div>
            <div>
              <LeResultsListItem
                v-for="(dictionary, index) in props.suggestedDictionaries"
                :key="dictionary.id"
                class="le-narrow-dictionary-selector__item"
                show-favorite-toggle
                :dictionary="dictionary"
                :is-selected="isSelected(dictionary.id)"
                :is-favorite="isFavorite(dictionary.id)"
                @favorite="favorite(dictionary.id)"
                @unfavorite="unfavorite(dictionary.id)"
                @select="selectSuggestedDictionary(dictionary.id, index + 1, close)"
              />
              <LeResultsListItem
                v-for="(dictionary, index) in props.resultUpsellDictionaries"
                :key="dictionary.id"
                class="le-narrow-dictionary-selector__item"
                is-upsell
                :is-unlocked="props.resultUpsellUnlockedDictionaries.includes(dictionary)"
                :dictionary="dictionary"
                :is-selected="isSelected(dictionary.id)"
                @select="selectSuggestedDictionary(dictionary.id, index + 1, close)"
              />
            </div>
          </div>
        </div>
      </PopoverPanel>
    </Transition>
  </Popover>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";
@import "@/assets/styles/z-index.less";

.le-narrow-dictionary-selector-button {
  margin-bottom: 8px;
}

.le-selected-dictionary {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  width: 100%;
  height: 66px;
  padding: 5px 7.3px 5px 16px;
  border: solid 1px @colors[@secondary][600];
  border-radius: 4px;
  background: @colors[@neutrals][100];
  text-align: left;

  &:hover {
    background-color: @colors[@secondary][200];
  }

  &.focus-visible {
    .focus-outline(green);
  }

  &__item {
    overflow: hidden;
    white-space: nowrap;
    mask-image: linear-gradient(to right, black calc(100% - 50px), transparent 95%);

    &-title,
    &-publisher {
      width: 100%;
    }

    &-title {
      color: @colors[@neutrals][400];
      font-family: @font-family-body;
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: 22px;
    }

    &-publisher {
      color: @colors[@secondary][800];
      font-family: @font-family-body;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px;
    }
  }

  &__icon {
    display: flex;
    align-self: center;
    margin-right: 10px;
    color: @colors[@secondary][800];
    justify-self: end;

    &:hover {
      color: @colors[@primary][500];
    }
  }
}

.le-narrow-dictionary-selector-container {
  position: relative;
  z-index: @le-narrow-dictionary-selector__container-z-index;
}

.le-narrow-dictionary-selector {
  position: absolute;
  width: 100%;
  max-height: 60vh;
  margin-top: 8px;
  padding-bottom: 8px;
  overflow-y: scroll;
  border: solid 1px @colors[@secondary][600];
  border-radius: 4px;
  background: white;
  box-shadow: 0px 11px 12px rgba(32, 43, 33, 0.25);

  &__transition-enter-active,
  &__transition-leave-active {
    transition: opacity 75ms ease-out;
  }

  &__transition-enter-from,
  &__transition-leave-to {
    opacity: 0;
  }

  &__collection-header {
    margin: 0 16px 0 16px;
    padding: 16px 0 14px 0;
    border-bottom: solid 1.87px #dfecea;
    color: #3f5155;
    font-family: @font-family-body;
    font-size: 16px;
    font-weight: 700;
    line-height: 11px;
  }

  &__collection + &__collection {
    margin-top: 12px;
  }

  &__item {
    margin: 0 12px;
    border-radius: 4px;

    & + & {
      margin-top: 4px;
    }

    &:first-child {
      margin-top: 8px;
    }
  }
}
</style>
